import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  Fieldset,
  Heading,
  Paragraph,
  Radio,
} from '@digdir/designsystemet-react';
import type { EvaluationReadiness } from '../../../api/workEnvironmentTypes';
import { useStartEvaluationMutation } from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { longDate } from '../../Settings/privacyView';
import { RESPONSE_DAYS } from '../evaluationView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = {
  surveyId: number;
  readiness: EvaluationReadiness;
  canStart: boolean;
  /** A changed plan waits for a new approval */
  waiting: boolean;
};

/** Step 9 starts from here: the team answers the same survey again */
export const EvaluationStartCard = ({
  surveyId,
  readiness,
  canStart,
  waiting,
}: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState<number>(RESPONSE_DAYS[1]);
  const start = useStartEvaluationMutation(surveyId);

  const text = readiness.reason
    ? t(`workEnvironmentEvaluation.start.${readiness.reason}`)
    : waiting
      ? t('workEnvironmentEvaluation.start.waitingApproval')
      : readiness.from
        ? t('workEnvironmentEvaluation.start.waiting', {
            date: longDate(readiness.from, i18n.language),
          })
        : t('workEnvironmentEvaluation.start.waitingNoDate');

  return (
    <section
      aria-labelledby="evaluation-start-heading"
      className={process.card}
    >
      <div className={process.cardBody}>
        <div className={process.sideHeading}>
          <span className={`ds-label ${process.eyebrow}`} data-size="sm">
            {t('workEnvironmentEvaluation.start.eyebrow')}
          </span>
          <Heading level={2} data-size="sm" id="evaluation-start-heading">
            {t('workEnvironmentEvaluation.start.title')}
          </Heading>
        </div>
        <Paragraph data-size="sm">{text}</Paragraph>
        {readiness.canStart && canStart && (
          <div>
            <Button variant="primary" onClick={() => setOpen(true)}>
              {t('workEnvironmentEvaluation.start.action')}
            </Button>
          </div>
        )}
      </div>
      {open && (
        <Dialog
          open
          onClose={() => setOpen(false)}
          closedby="any"
          closeButton={t('workEnvironmentRisk.data.close')}
        >
          <div className={classes.areaBody}>
            <Heading level={2} data-size="xs">
              {t('workEnvironmentEvaluation.start.action')}
            </Heading>
            <Paragraph>
              {t('workEnvironmentEvaluation.start.dialogText')}
            </Paragraph>
            <Fieldset data-size="sm">
              <Fieldset.Legend>
                {t('workEnvironmentEvaluation.start.deadline')}
              </Fieldset.Legend>
              {RESPONSE_DAYS.map((option) => (
                <Radio
                  key={option}
                  name="evaluation-days"
                  label={t('workEnvironmentEvaluation.start.days', {
                    count: option,
                  })}
                  value={String(option)}
                  checked={days === option}
                  onChange={() => setDays(option)}
                />
              ))}
            </Fieldset>
            <Paragraph data-size="sm" className={process.subtle}>
              {t('workEnvironmentEvaluation.start.anonymous')}
            </Paragraph>
            {start.isError && (
              <Alert data-color="danger" data-size="sm">
                <Paragraph data-size="sm">
                  {t('workEnvironmentEvaluation.start.failed')}
                </Paragraph>
              </Alert>
            )}
            <div className={classes.dialogButtons}>
              <Button
                variant="primary"
                data-size="sm"
                loading={start.isPending}
                onClick={() =>
                  start.mutate(days, { onSuccess: () => setOpen(false) })
                }
              >
                {t('workEnvironmentEvaluation.start.send')}
              </Button>
              <Button
                variant="tertiary"
                data-size="sm"
                onClick={() => setOpen(false)}
              >
                {t('workEnvironmentEvaluation.start.cancel')}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </section>
  );
};
